import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { SeverityBadge } from "../components/atoms/SeverityBadge";
import { IncidentTimeline } from "../components/organisms/IncidentTimeline";
import type { Incident } from "../types/incidents";
import { apiFetch } from "../lib/config";

interface CorrelatedSignals {
  logs: Array<{ timestamp: string; level: string; message: string }>;
  metrics: Array<{ name: string; timestamp: string; value: number }>;
  workflow_executions: Array<{ id: string; rule_name: string; status: string }>;
}

function SignalsPanel({ incidentId }: { incidentId: string }) {
  const [signals, setSignals] = useState<CorrelatedSignals | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch(`/api/v1/telemetry/correlate/?incident_id=${incidentId}`)
      .then((r) => r.json())
      .then((d) => setSignals(d))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [incidentId]);

  if (loading) return <div className="aptean-skeleton" style={{ height: 60 }} aria-busy="true" />;
  if (error) return <p style={{ color: "var(--aptean-error, #e74c3c)" }}>Error: {error}</p>;
  if (!signals || (signals.logs.length === 0 && signals.metrics.length === 0 && signals.workflow_executions.length === 0)) {
    return <p style={{ color: "var(--aptean-text-secondary)" }}>No correlated signals found.</p>;
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {signals.logs.length > 0 && (
        <div>
          <h3 style={{ margin: "0 0 8px", fontSize: "0.9rem", color: "var(--aptean-text-secondary)", textTransform: "uppercase" }}>Related Logs</h3>
          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 4 }}>
            {signals.logs.map((log, i) => (
              <li key={i} style={{ display: "flex", gap: 8, fontSize: "0.8rem" }}>
                <span style={{ color: "var(--aptean-text-secondary)" }}>{new Date(log.timestamp).toLocaleTimeString()}</span>
                <span style={{ padding: "0 6px", borderRadius: 4, background: log.level === "error" ? "var(--aptean-error, #e74c3c)" : "var(--aptean-surface-2)", color: log.level === "error" ? "#fff" : "var(--aptean-text-primary)", fontSize: "0.7rem", fontWeight: 600 }}>{log.level}</span>
                <span style={{ color: "var(--aptean-text-primary)", flex: 1 }}>{log.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {signals.metrics.length > 0 && (
        <div>
          <h3 style={{ margin: "0 0 8px", fontSize: "0.9rem", color: "var(--aptean-text-secondary)", textTransform: "uppercase" }}>Metric Anomalies</h3>
          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 4 }}>
            {signals.metrics.map((m, i) => (
              <li key={i} style={{ display: "flex", gap: 8, fontSize: "0.8rem", color: "var(--aptean-text-primary)" }}>
                <span style={{ fontWeight: 600 }}>{m.name}</span>
                <span style={{ color: "var(--aptean-text-secondary)" }}>{new Date(m.timestamp).toLocaleTimeString()}</span>
                <span style={{ color: "var(--aptean-error, #e74c3c)" }}>{m.value}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {signals.workflow_executions.length > 0 && (
        <div>
          <h3 style={{ margin: "0 0 8px", fontSize: "0.9rem", color: "var(--aptean-text-secondary)", textTransform: "uppercase" }}>Workflow Executions</h3>
          <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
            {signals.workflow_executions.map((ex) => (
              <li key={ex.id} style={{ fontSize: "0.85rem", color: "var(--aptean-text-primary)" }}>
                {ex.rule_name} — {ex.status}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function IncidentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [incident, setIncident] = useState<Incident | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"details" | "signals">("details");

  useEffect(() => {
    if (!id) return;
    apiFetch(`/api/v1/incidents/${id}/`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        setIncident(data);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load incident");
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <main className="aptean-page aptean-incident-detail">
        <div className="aptean-skeleton aptean-skeleton--title" aria-busy="true" />
        <div className="aptean-skeleton aptean-skeleton--body" aria-busy="true" />
      </main>
    );
  }

  if (error || !incident) {
    return (
      <main className="aptean-page aptean-incident-detail">
        <p role="alert" className="aptean-error">
          {error ?? "Incident not found"}
        </p>
        <Link to="/status" className="aptean-back-link">
          ← Back to status
        </Link>
      </main>
    );
  }

  return (
    <main className="aptean-page aptean-incident-detail">
      <Link to="/status" className="aptean-back-link">
        ← Back to status
      </Link>

      <header className="aptean-incident-detail__header">
        <h1 className="aptean-incident-detail__title">{incident.title}</h1>
        <SeverityBadge severity={incident.severity} />
        <span
          className={`aptean-incident-detail__status aptean-incident-detail__status--${incident.status}`}
        >
          {incident.status}
        </span>
      </header>

      <div className="aptean-incident-detail__meta">
        <div>
          <strong>Created:</strong>{" "}
          <time dateTime={incident.created_at}>
            {new Date(incident.created_at).toLocaleString()}
          </time>
        </div>
        {incident.resolved_at && (
          <div>
            <strong>Resolved:</strong>{" "}
            <time dateTime={incident.resolved_at}>
              {new Date(incident.resolved_at).toLocaleString()}
            </time>
          </div>
        )}
      </div>

      {incident.affected_services.length > 0 && (
        <section className="aptean-incident-detail__services">
          <h2>Affected Services</h2>
          <ul>
            {incident.affected_services.map((s, i) => (
              <li key={i}>
                {s.service_name} / {s.region_name}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, borderBottom: "2px solid var(--aptean-border, #2a2a3a)", marginBottom: 16 }}>
        {(["details", "signals"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: "8px 16px",
              background: "none",
              border: "none",
              borderBottom: activeTab === tab ? "2px solid var(--aptean-accent, #54B3BE)" : "2px solid transparent",
              marginBottom: -2,
              cursor: "pointer",
              color: activeTab === tab ? "var(--aptean-accent, #54B3BE)" : "var(--aptean-text-secondary)",
              fontWeight: activeTab === tab ? 600 : 400,
              textTransform: "capitalize",
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === "details" && (
        <section className="aptean-incident-detail__timeline">
          <h2>Updates</h2>
          {incident.updates.length === 0 ? (
            <p>No updates yet.</p>
          ) : (
            <IncidentTimeline updates={incident.updates} />
          )}
        </section>
      )}

      {activeTab === "signals" && id && (
        <section className="aptean-incident-detail__signals">
          <h2>Correlated Signals</h2>
          <SignalsPanel incidentId={id} />
        </section>
      )}
    </main>
  );
}
